import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import KategorienOverview from '../components/KategorienOverview';
import ServiceSection from '../components/ServiceSection';
import NeuheitenSection from '../components/NeuheitenSection';
import PublicAvailabilitySection from '../components/PublicAvailabilitySection';
import GoogleReviewsSection from '../components/GoogleReviewsSection';
import CustomerPhotoGallery from '../components/CustomerPhotoGallery';
import FinalCTASection from '../components/FinalCTASection';
import ResponsiveImage from '../components/ResponsiveImage';
import CloudDivider from '../components/CloudDivider';
import QuizSection from '../components/QuizSection';
import AISearchSection from '../components/AISearchSection';
import PollSection from '../components/PollSection';
import HomeCountdownSlider from '../components/HomeCountdownSlider';
import HomeRodeoBanner from '../components/HomeRodeoBanner';
import HomeTrainBanner from '../components/HomeTrainBanner';
import HomePromoCarousel from '../components/HomePromoCarousel';
import HomeFAQTeaser from '../components/HomeFAQTeaser';
import ExclusiveSection from '../components/ExclusiveSection';
import PromoSlider from '../components/PromoSlider';
import QuickInquiryModal from '../components/QuickInquiryModal';
import SEO from '../components/SEO';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import * as Icons from 'lucide-react';
import { useSettings, resolveImageUrl } from '../context/SettingsContext';
import { isVideoUrl } from '../components/ImageUpload';

const DynamicIcon = ({ name, className }) => {
  const Icon = Icons[name] || Icons.CheckCircle;
  return <Icon className={className} />;
};

const Home = ({ variant = null }) => {
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [heroInquiryOpen, setHeroInquiryOpen] = React.useState(false);

  const home = settings?.home || {};
  const contact = settings?.contact || {};
  // Start from the global about-section, then merge variant overrides on top
  const defaultAbout = home.about_section || {};
  // Variant fields fall back to home/startpage defaults whenever they are empty
  // (admins often leave Landing-Page images blank to inherit the Startseite look).
  const about = variant
    ? {
        ...defaultAbout,
        eyebrow: variant.hero_eyebrow || defaultAbout.eyebrow,
        title: variant.hero_title || defaultAbout.title,
        paragraphs: variant.hero_text ? [variant.hero_text] : defaultAbout.paragraphs,
        image_1: variant.hero_image_1 || defaultAbout.image_1,
        image_2: variant.hero_image_2 || defaultAbout.image_2,
      }
    : defaultAbout;
  const advantages = home.advantages || [];

  // Hero-Hintergrund zentral aus Startseiten-Einstellungen (gilt auch für alle Landingpages)
  const DEFAULT_HERO_BG = 'https://customer-assets.emergentagent.com/job_bounce-castle-rental-1/artifacts/vwd8dau4_grafik.png';
  const heroBgStyle = home.hero_bg_image
    ? { backgroundImage: `url(${resolveImageUrl(home.hero_bg_image)})` }
    : home.hero_bg_color
      ? { backgroundColor: home.hero_bg_color }
      : { backgroundImage: `url(${DEFAULT_HERO_BG})` };

  const ctaText = variant?.hero_cta_text || 'Produkte ansehen';
  const ctaLink = variant?.hero_cta_link || '/produkte';

  const colorClasses = {
    sky: 'text-sky-500',
    yellow: 'text-yellow-400',
    orange: 'text-orange-500',
    red: 'text-rose-500',
    green: 'text-emerald-500',
  };

  // Kurze Vorstellung = erster Absatz von about
  const introText = (about.paragraphs && about.paragraphs[0]) || '';

  // SEO FAQ schema – default questions that drive Google rich-snippets for the homepage.
  // Admins can override by editing settings.seo.home.faqs in the future.
  const seoFaqs = (settings?.seo?.home?.faqs && Array.isArray(settings.seo.home.faqs))
    ? settings.seo.home.faqs
    : [
        { q: 'Wo kann ich Hüpfburgen in Schwerin mieten?', a: 'Bei KidsEvent Schwerin – wir liefern Hüpfburgen, Bullriding, Bungee-Trampolin und vieles mehr in Schwerin, Westmecklenburg und Lübeck. Online Termin auswählen und in wenigen Klicks unverbindlich anfragen.' },
        { q: 'Wie viel kostet eine Hüpfburg-Miete?', a: 'Die Tagespreise starten ab ca. 120 € pro Hüpfburg. Mit Auf-/Abbau-Service und Endreinigung als Komplett-Service nochmals 75 € pro Hüpfburg. Genaue Preise siehst du direkt beim jeweiligen Produkt.' },
        { q: 'Liefert ihr auch außerhalb von Schwerin?', a: 'Ja – Standard-Liefergebiet sind Schwerin, Westmecklenburg und Lübeck. Für entferntere Orte berechnen wir eine Kilometer-Pauschale, die du im Buchungsformular sofort siehst.' },
        { q: 'Was passiert bei schlechtem Wetter?', a: 'Bei sehr starkem Wind, Gewitter oder Dauerregen vereinbaren wir mit dir einen kostenlosen Ausweichtermin. Sicherheit geht vor – keine Stornogebühren bei echten Unwetter-Warnungen.' },
        { q: 'Brauche ich Strom für die Hüpfburg?', a: 'Ja – jede Hüpfburg benötigt eine 230V-Steckdose im Umkreis von ca. 25 Metern. Eine Kabeltrommel können wir auf Wunsch kostenlos mitliefern.' },
      ];

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {variant?.seo_title ? (
        <Helmet>
          <title>{variant.seo_title}</title>
          <meta name="description" content={variant.seo_description || ''} />
          {variant.canonical && <link rel="canonical" href={variant.canonical} />}
          <meta property="og:title" content={variant.seo_title} />
          <meta property="og:description" content={variant.seo_description || ''} />
        </Helmet>
      ) : (
        <SEO page="home" faqs={seoFaqs} />
      )}
      <Header />

      <main className="flex-1">
        {/* Hero mit Aquarell-Blau Hintergrund + integrierte Wellen */}
        <section
          className="relative pt-14 md:pt-20 pb-14 md:pb-20 overflow-hidden"
          data-testid="intro-section"
          style={{
            ...heroBgStyle,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          {/* Top wave (blau vom Header in den Aquarell-Hintergrund) */}
          <svg
            className="absolute left-0 w-full h-10 md:h-14 block"
            viewBox="0 0 1440 100"
            preserveAspectRatio="none"
            style={{ display: 'block', transform: 'rotate(180deg)', top: '-2px' }}
            aria-hidden="true"
          >
            <path
              fill="#005898"
              d="M0,50 C 240,50 320,25 480,25 C 640,25 720,50 960,50 C 1200,50 1280,25 1440,25 L1440,100 L0,100 Z"
            />
          </svg>
          {/* Bottom wave (Aquarell in weiß) */}
          <svg
            className="absolute left-0 w-full h-12 md:h-20 pointer-events-none block"
            viewBox="0 0 1440 100"
            preserveAspectRatio="none"
            style={{ display: 'block', bottom: '-2px' }}
            aria-hidden="true"
          >
            <path
              fill="#ffffff"
              d="M0,50 C 240,50 320,25 480,25 C 640,25 720,50 960,50 C 1200,50 1280,25 1440,25 L1440,100 L0,100 Z"
            />
          </svg>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <div className="space-y-5">
                {about.eyebrow && (
                  <span className="inline-block bg-white/80 backdrop-blur-sm text-sky-700 font-bold text-xs uppercase tracking-widest px-4 py-2 rounded-full shadow-sm">
                    {about.eyebrow}
                  </span>
                )}
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-sky-700 leading-tight">
                  {about.title || 'Für jedes Event die passende Burg'}
                </h1>
                <p className="text-base md:text-lg text-sky-900/90 leading-relaxed">
                  {introText}
                </p>
                <div className="inline-flex flex-col gap-3 max-w-full pt-2">
                <div className="flex flex-wrap gap-2 sm:gap-3">
                  <Button
                    className="bg-yellow-400 hover:bg-yellow-500 text-sky-900 font-bold rounded-full px-5 sm:px-7 py-4 sm:py-5 text-sm sm:text-base shadow-lg lift-on-hover"
                    onClick={() => navigate(ctaLink)}
                    data-testid="intro-cta-products"
                  >
                    {ctaText}
                  </Button>
                  {contact.phone && (
                    <a href={`tel:${contact.phone.replace(/\s/g, '')}`}>
                      <Button variant="outline" className="rounded-full px-5 sm:px-7 py-4 sm:py-5 text-sm sm:text-base border-2 border-sky-500 text-sky-700 hover:bg-sky-50 font-semibold bg-white">
                        <Icons.Phone className="h-4 w-4 mr-2" />
                        Anrufen
                      </Button>
                    </a>
                  )}
                  <Button
                    variant="outline"
                    className="hidden sm:inline-flex rounded-full px-5 sm:px-7 py-4 sm:py-5 text-sm sm:text-base border-2 border-sky-500 text-sky-700 hover:bg-sky-50 font-semibold bg-white"
                    onClick={() => setHeroInquiryOpen(true)}
                    data-testid="intro-cta-inquiry"
                  >
                    <Icons.Mail className="h-4 w-4 mr-2" />
                    Kontaktformular
                  </Button>
                </div>
                <AISearchSection inline />
                </div>
              </div>

              <div className="relative h-[500px] md:h-[560px]">
                {about.image_1 && (
                  <div className="absolute top-0 left-0 w-[68%] tilted-card tilted-card-left z-10">
                    <div className="w-full h-72 md:h-[340px] rounded-3xl shadow-2xl border-4 border-white bg-neutral-100 overflow-hidden">
                      {isVideoUrl(about.image_1) ? (
                        <video
                          src={resolveImageUrl(about.image_1)}
                          className="w-full h-full object-cover block"
                          autoPlay muted loop playsInline preload="metadata"
                          data-testid="hero-video-1"
                        />
                      ) : (
                        <ResponsiveImage
                          src={about.image_1}
                          alt="Hüpfburg"
                          sizes="(min-width: 1024px) 480px, 60vw"
                          className="w-full h-full object-cover block"
                          priority
                        />
                      )}
                    </div>
                  </div>
                )}
                {about.image_2 && (
                  <div className="absolute bottom-0 right-0 w-[68%] tilted-card tilted-card-right z-20">
                    <div className="w-full h-72 md:h-[340px] rounded-3xl shadow-2xl border-4 border-white bg-neutral-100 overflow-hidden">
                      {isVideoUrl(about.image_2) ? (
                        <video
                          src={resolveImageUrl(about.image_2)}
                          className="w-full h-full object-cover block"
                          autoPlay muted loop playsInline preload="metadata"
                          data-testid="hero-video-2"
                        />
                      ) : (
                        <ResponsiveImage
                          src={about.image_2}
                          alt="Hüpfburg Event"
                          sizes="(min-width: 1024px) 480px, 60vw"
                          className="w-full h-full object-cover block"
                          priority
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Dynamische Sektionen – Reihenfolge & Sichtbarkeit aus settings.home.sections_order */}
        <DynamicSections home={home} advantages={advantages} colorClasses={colorClasses} />

        {/* Weiße Trennlinie zwischen letzter Sektion und Footer (optische Trennung ohne weiße Lücke). */}
        <div className="h-1.5 w-full bg-white" aria-hidden="true" data-testid="footer-separator" />
      </main>

      <Footer />
      <QuickInquiryModal open={heroInquiryOpen} onClose={() => setHeroInquiryOpen(false)} />
    </div>
  );
};

export default Home;

// ============== DYNAMIC SECTION RENDERER ==============
// `rodeo_banner` und `train_banner` sind veraltete Section-Keys — sie leben
// jetzt ausschließlich im `promo_carousel`. In DEFAULT_ORDER werden sie NICHT
// mehr aufgeführt, und in `mergedSectionsOrder` werden sie aus persistierten
// Alt-Daten herausgefiltert, damit sie keine leeren Sections + überflüssige
// CloudDivider mehr erzeugen.
const OBSOLETE_SECTION_KEYS = new Set(['rodeo_banner', 'train_banner', 'ai_search']);

const DEFAULT_ORDER = [
  { key: 'availability', visible: true },
  { key: 'quiz', visible: true },
  { key: 'service', visible: true },
  { key: 'promo_slider', visible: true },
  { key: 'categories', visible: true },
  { key: 'countdown', visible: true },
  { key: 'neuheiten', visible: true },
  { key: 'exclusive', visible: true },
  { key: 'advantages', visible: true },
  { key: 'poll', visible: true },
  { key: 'promo_carousel', visible: true },
  { key: 'reviews', visible: true },
  { key: 'customer_photos', visible: true },
  { key: 'faq_teaser', visible: true },
  { key: 'final_cta', visible: true },
];

// Phase 125.28 — Merge helper: fügt fehlende bekannte Sektionen automatisch an
// die persistente Reihenfolge an, damit neue Startseiten-Blöcke ohne
// Admin-Intervention sichtbar werden.
const mergedSectionsOrder = (persistedOrder) => {
  const persisted = Array.isArray(persistedOrder) && persistedOrder.length > 0
    ? persistedOrder
    : DEFAULT_ORDER;
  // Veraltete Keys (rodeo_banner, train_banner) aus persistierten Alt-Daten entfernen
  const cleaned = persisted.filter((s) => !OBSOLETE_SECTION_KEYS.has(s.key));
  const known = DEFAULT_ORDER.map((s) => s.key);
  const persistedKeys = new Set(cleaned.map((s) => s.key));
  const result = [...cleaned];
  known.forEach((k, i) => {
    if (persistedKeys.has(k)) return;
    const prevIdx = i > 0 ? result.findIndex((s) => s.key === known[i - 1]) : -1;
    result.splice(prevIdx >= 0 ? prevIdx + 1 : result.length, 0, { key: k, visible: true });
    persistedKeys.add(k);
  });
  return result;
};

// Full-Width Slider-Sektionen mit eigenen Wellen — sie überspringen die
// alternierende Hintergrund-Rhythmik komplett und färben ihre Wellen
// dynamisch auf die Farbe der Vorgänger-/Nachfolger-Sektion.
// `final_cta` gehört ebenfalls dazu, da sie ihren eigenen dunkelblauen
// Hintergrund (#005898) mitbringt und über explizite CloudDivider die
// Übergänge zu den Nachbar-Sektionen zeichnet.
const TRANSPARENT_KEYS = new Set(['countdown', 'promo_carousel', 'final_cta']);

const FINAL_CTA_BG = '#005898';

// Alternating background colors: position-based, NOT tied to section type.
// Position 0 = white, 1 = blue, 2 = white, … so the visual rhythm always
// stays the same even when sections are reordered.
const ALT_BG = ['#ffffff', '#f0f9ff'];

// Phase 128.65 — Kunden-Fotos sitzen bewusst auf reinem Weiß, damit sie
// direkt in die weiße Wellen-Zone der vorhergehenden Sektion "hineinfließen"
// und kein zusätzlicher Farb-Übergang entsteht.
const SECTION_BG_OVERRIDE = {};

// Meldet, ob eine Sektion leer gerendert wurde (Höhe 0), damit sie nicht im Farbwechsel mitzählt.
const MeasuredWrap = ({ bg, testId, onEmpty, children }) => {
  const ref = React.useRef(null);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => onEmpty(el.offsetHeight === 0));
    ro.observe(el);
    return () => ro.disconnect();
  }, [onEmpty]);
  return <div ref={ref} style={{ backgroundColor: bg }} data-testid={testId}>{children}</div>;
};

const DynamicSections = ({ home, advantages, colorClasses }) => {
  const [emptyKeys, setEmptyKeys] = React.useState({});
  const emptyHandlers = React.useRef({});
  const getEmptyHandler = (key) => {
    if (!emptyHandlers.current[key]) {
      emptyHandlers.current[key] = (isEmpty) =>
        setEmptyKeys((prev) => (!!prev[key] === isEmpty ? prev : { ...prev, [key]: isEmpty }));
    }
    return emptyHandlers.current[key];
  };
  const order = mergedSectionsOrder(home.sections_order);
  const allVisible = order.filter((s) => s.visible !== false);
  const visible = allVisible.filter((s) => !emptyKeys[s.key]);

  // Berechne pro Section den Slot-Index (nur nicht-transparente zählen)
  let slotCounter = 0;
  const enriched = visible.map((s) => {
    const transparent = TRANSPARENT_KEYS.has(s.key);
    const info = { key: s.key, transparent, slot: transparent ? null : slotCounter };
    if (!transparent) slotCounter += 1;
    return info;
  });
  const bgOfSection = (info) => {
    if (!info || info.transparent) return '#ffffff';
    if (SECTION_BG_OVERRIDE[info.key]) return SECTION_BG_OVERRIDE[info.key];
    return ALT_BG[info.slot % 2];
  };

  // Hilfsfunktion: Farbe der ersten nicht-transparenten Sektion vor/nach index.
  // Wenn die DIREKTE Nachbarsektion transparent ist, geben wir `null` zurück,
  // d.h. der Slider zeichnet auf dieser Seite keine Welle (kein Doppel-Wellen-Effekt).
  const findNeighborBg = (arrIdx, direction) => {
    const step = direction === 'prev' ? -1 : 1;
    const directIdx = arrIdx + step;
    if (directIdx >= 0 && directIdx < enriched.length && enriched[directIdx].transparent) {
      return null; // Nachbar ist auch ein transparenter Slider → keine Welle nötig
    }
    for (let j = directIdx; j >= 0 && j < enriched.length; j += step) {
      if (!enriched[j].transparent) return bgOfSection(enriched[j]);
    }
    // Nichts gefunden — Randfarbe: oben = #ffffff (Header/weiß), unten = #005898 (Footer)
    return direction === 'prev' ? '#ffffff' : '#005898';
  };

  return (
    <>
      {allVisible.map((raw) => {
        const arrIdx = enriched.findIndex((e) => e.key === raw.key);
        if (arrIdx === -1) {
          return (
            <React.Fragment key={raw.key}>
              {false}
              {renderSection(raw.key, home, advantages, colorClasses, 'transparent', getEmptyHandler(raw.key))}
            </React.Fragment>
          );
        }
        const s = enriched[arrIdx];
        if (s.transparent) {
          const topBg = findNeighborBg(arrIdx, 'prev');
          const bottomBg = findNeighborBg(arrIdx, 'next');
          // Kein Wrapper-BG, kein CloudDivider — der Slider bringt seine
          // eigenen Wellen mit und färbt sie auf die Nachbar-Farben ein.
          return (
            <React.Fragment key={s.key}>
              {renderTransparentSlider(s.key, home, topBg, bottomBg)}
            </React.Fragment>
          );
        }
        const myBg = bgOfSection(s);
        const prevBg = findNeighborBg(arrIdx, 'prev');
        // Exclusive-Sektion zeichnet ihre EIGENEN oberen/unteren Wellen (das Bild
        // beginnt/endet exakt an der Welle). Daher: kein externer CloudDivider
        // ober-/unterhalb, aber die Sektion bleibt in der Farb-Rhythmik gezählt.
        if (s.key === 'exclusive') {
          const nextBg = findNeighborBg(arrIdx, 'next');
          return (
            <React.Fragment key={s.key}>
              <div data-testid="sect-exclusive">
                <ExclusiveSection
                  cfg={home.exclusive}
                  topBg={prevBg == null ? '#ffffff' : prevBg}
                  bottomBg={nextBg == null ? '#ffffff' : nextBg}
                />
              </div>
            </React.Fragment>
          );
        }
        // Divider nur setzen, wenn der direkte Vorgänger KEIN transparenter
        // Slider ist (sonst zeichnet der Slider bereits die Übergangs-Welle).
        const prevIsTransparent = arrIdx > 0 && enriched[arrIdx - 1].transparent;
        const prevIsExclusive = arrIdx > 0 && enriched[arrIdx - 1].key === 'exclusive';
        return (
          <React.Fragment key={s.key}>
            {!prevIsTransparent && !prevIsExclusive && prevBg !== myBg && <CloudDivider topColor={prevBg} bottomColor={myBg} />}
            {renderSection(s.key, home, advantages, colorClasses, myBg, getEmptyHandler(s.key))}
          </React.Fragment>
        );
      })}
    </>
  );
};

// Full-Width Slider rendern (ohne Wrapper-BG, mit expliziten top/bottom Wellenfarben).
const renderTransparentSlider = (key, home, topBg, bottomBg) => {
  switch (key) {
    case 'countdown':
      return (
        <div data-testid="sect-countdown">
          <HomeCountdownSlider config={home.countdown_slider} topBg={topBg} bottomBg={bottomBg} />
        </div>
      );
    case 'promo_carousel':
      return (
        <div data-testid="sect-promo-carousel">
          <HomePromoCarousel
            rodeoConfig={home.rodeo_banner}
            trainConfig={home.train_banner}
            topBg={topBg}
            bottomBg={bottomBg}
          />
        </div>
      );
    case 'final_cta':
      return (
        <div data-testid="sect-final-cta">
          {/* Wenn Nachbar ein transparenter Slider ist, ist topBg/bottomBg null → keine Doppel-Welle */}
          {topBg !== null && topBg !== FINAL_CTA_BG && <CloudDivider topColor={topBg} bottomColor={FINAL_CTA_BG} />}
          <FinalCTASection />
          {bottomBg !== null && bottomBg !== FINAL_CTA_BG && <CloudDivider topColor={FINAL_CTA_BG} bottomColor={bottomBg} />}
        </div>
      );
    default:
      return null;
  }
};

const renderSection = (key, home, advantages, colorClasses, bg, onEmpty) => {
  // Wrap every section in a div with the alternating background so component-internal
  // bg classes don't break the rhythm when sections are moved around.
  const wrapper = (children, testId) => (
    <MeasuredWrap bg={bg} testId={testId} onEmpty={onEmpty}>{children}</MeasuredWrap>
  );
  switch (key) {
    case 'service': return wrapper(<ServiceSection />, 'sect-service');
    case 'promo_slider': return wrapper(<PromoSlider />, 'sect-promo-slider');
    case 'availability': return wrapper(<PublicAvailabilitySection />, 'sect-availability');
    case 'quiz': return wrapper(<QuizSection />, 'sect-quiz');
    case 'categories': return wrapper(<KategorienOverview />, 'sect-categories');
    case 'neuheiten': return wrapper(<NeuheitenSection />, 'sect-neuheiten');
    case 'poll': return wrapper(<PollSection compact variant={home.poll_variant === 'creative' ? 'creative' : 'classic'} />, 'sect-poll');
    case 'reviews': return wrapper(<GoogleReviewsSection />, 'sect-reviews');
    case 'customer_photos': return wrapper(<CustomerPhotoGallery />, 'sect-customer-photos');
    case 'faq_teaser': return wrapper(<HomeFAQTeaser />, 'sect-faq-teaser');
    case 'exclusive': return wrapper(<ExclusiveSection cfg={home.exclusive} />, 'sect-exclusive');
    case 'advantages':
      return wrapper(
        <section className="py-4 md:py-8" data-testid="advantages-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center space-y-2 mb-6 md:mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-sky-700">
                {home.advantages_title || 'Warum uns wählen?'}
              </h2>
              {home.advantages_subtitle && (
                <p className="text-lg text-neutral-600 leading-relaxed max-w-2xl mx-auto">
                  {home.advantages_subtitle}
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
              {(advantages || []).map((advantage, index) => (
                <div
                  key={index}
                  className="bg-white/70 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-4 sm:p-7 text-center space-y-2 sm:space-y-3 hover:shadow-lg transition-shadow border border-sky-100"
                  data-testid={`advantage-${index}`}
                >
                  <div className="flex justify-center">
                    <DynamicIcon
                      name={advantage.icon}
                      className={`h-8 w-8 sm:h-10 sm:w-10 ${colorClasses[advantage.color] || 'text-sky-500'}`}
                    />
                  </div>
                  <h3 className="text-[15px] sm:text-lg font-bold text-sky-700 leading-tight">{advantage.title}</h3>
                  <p className="text-[13px] sm:text-sm text-neutral-600 leading-relaxed">{advantage.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>,
        'sect-advantages'
      );
    default: return null;
  }
};
